import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { History, Plus, SendHorizontal, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { AI_UNAVAILABLE, useAiStatus, type ChatMessage, type ChatSummary } from '../lib/ai';
import { timeAgo } from '../lib/format';
import { Button, Drawer, cx } from './ui';

const SUGGESTIONS = [
  'Which of my leads are hot right now?',
  'Show my overdue follow-ups',
  'How did the team do on calls this week?',
  'How many leads came from each source this month?',
];

/** Floating "Ask AI" button + chat panel. Answers come from the company's own CRM data. */
export function AskAi() {
  const { can } = useAuth();
  const status = useAiStatus();
  const [open, setOpen] = useState(false);
  if (!can('ai.use') || !status.data || status.data.reason === 'not_set_up') return null;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed right-4 bottom-4 z-30 flex h-12 items-center gap-2 rounded-full bg-brand-600 px-4 font-medium text-white shadow-lg hover:bg-brand-700 sm:right-6 sm:bottom-6"
      >
        <Sparkles className="size-5" /> Ask AI
      </button>
      {open && <ChatPanel onClose={() => setOpen(false)} />}
    </>
  );
}

function ChatPanel({ onClose }: { onClose: () => void }) {
  const status = useAiStatus();
  const qc = useQueryClient();
  const [chatId, setChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  const history = useQuery({ queryKey: ['ai', 'chats'], queryFn: () => api.get<ChatSummary[]>('/ai/chats'), enabled: showHistory });
  useEffect(() => { end.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  const ask = async (question: string) => {
    const q = question.trim();
    if (!q || busy) return;
    setText('');
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setBusy(true);
    try {
      const r = await api.post<{ id: string; messages: ChatMessage[] }>('/ai/chats', { chatId, text: q });
      setChatId(r.id);
      setMessages(r.messages);
      void qc.invalidateQueries({ queryKey: ['ai', 'chats'] });
    } catch (e) {
      setMessages((m) => m.slice(0, -1));
      setText(q);
      toast.error(e instanceof Error ? e.message : 'AI couldn’t answer just now.');
    } finally { setBusy(false); }
  };
  const openChat = async (id: string) => {
    setShowHistory(false);
    const c = await api.get<{ id: string; messages: ChatMessage[] }>(`/ai/chats/${id}`);
    setChatId(c.id); setMessages(c.messages);
  };
  const remove = async (id: string) => {
    await api.del(`/ai/chats/${id}`);
    if (id === chatId) { setChatId(null); setMessages([]); }
    void history.refetch();
  };
  const unavailable = status.data && !status.data.ready && status.data.reason ? AI_UNAVAILABLE[status.data.reason] : null;

  return (
    <Drawer open onClose={onClose} title={<span className="flex items-center gap-2"><Sparkles className="size-5 text-brand-600" /> Ask AI</span>}
      description="Questions about your leads, follow-ups, calls and sales."
      headerExtra={<>
        <button onClick={() => setShowHistory((v) => !v)} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" title="Earlier chats" aria-label="Earlier chats"><History className="size-5" /></button>
        <button onClick={() => { setChatId(null); setMessages([]); setShowHistory(false); }} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" title="New chat" aria-label="New chat"><Plus className="size-5" /></button>
      </>}
      footer={
        <form className="flex w-full items-end gap-2" onSubmit={(e) => { e.preventDefault(); void ask(text); }}>
          <textarea
            value={text} rows={1} disabled={!!unavailable}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void ask(text); } }}
            placeholder={unavailable ? '' : 'Ask anything about your sales…'} aria-label="Your question"
            className="max-h-32 min-h-10 flex-1 resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-base focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 focus:outline-none sm:text-sm dark:border-slate-700 dark:bg-slate-900"
          />
          <Button type="submit" aria-label="Send" disabled={!text.trim() || !!unavailable} loading={busy} icon={<SendHorizontal className="size-4" />} />
        </form>
      }>
      {showHistory ? (
        <div className="space-y-1">
          <p className="mb-2 text-sm font-medium text-slate-500">Earlier chats</p>
          {history.data?.length === 0 && <p className="text-sm text-slate-500">No earlier chats.</p>}
          {history.data?.map((c) => (
            <div key={c.id} className="flex items-center gap-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
              <button onClick={() => openChat(c.id)} className="min-w-0 flex-1 px-3 py-2 text-left">
                <span className="block truncate text-sm font-medium">{c.title}</span>
                <span className="block text-xs text-slate-500">{timeAgo(c.updatedAt)}</span>
              </button>
              <button onClick={() => remove(c.id)} className="p-2 text-slate-400 hover:text-red-600" aria-label={`Delete “${c.title}”`}><Trash2 className="size-4" /></button>
            </div>
          ))}
        </div>
      ) : messages.length === 0 ? (
        <div>
          {unavailable && <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-200">{unavailable}</p>}
          <p className="text-sm text-slate-600 dark:text-slate-400">Try asking:</p>
          <div className="mt-3 flex flex-col items-start gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} disabled={!!unavailable} onClick={() => ask(s)} className="rounded-full bg-brand-50 px-3 py-1.5 text-left text-sm text-brand-800 hover:bg-brand-100 disabled:opacity-50 dark:bg-brand-500/15 dark:text-brand-200">
                {s}
              </button>
            ))}
          </div>
          <p className="mt-6 text-xs text-slate-500">AI only sees the leads and reports you can see. It can make mistakes — check important numbers.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={cx('flex', m.role === 'user' && 'justify-end')}>
              <div className={cx('max-w-[85%] rounded-2xl px-4 py-2.5 text-sm', m.role === 'user' ? 'bg-brand-600 text-white' : 'bg-slate-100 dark:bg-slate-800')}>
                {m.role === 'user' ? m.text : <RichText text={m.text} onNavigate={onClose} />}
              </div>
            </div>
          ))}
          {busy && <div className="flex"><div className="rounded-2xl bg-slate-100 px-4 py-3 dark:bg-slate-800"><span className="flex gap-1" aria-label="AI is thinking"><Dot /><Dot d="150ms" /><Dot d="300ms" /></span></div></div>}
          <div ref={end} />
        </div>
      )}
    </Drawer>
  );
}

const Dot = ({ d = '0ms' }: { d?: string }) => <span className="size-2 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: d }} />;

/** Tiny, safe formatter for AI answers: paragraphs, bullet lists, **bold**, and L-123 lead links. No HTML is ever injected. */
export function RichText({ text, onNavigate }: { text: string; onNavigate?: () => void }) {
  const links = (text: string): ReactNode[] =>
    text.split(/(\bL-\d+\b)/g).map((part, i) =>
      /^L-\d+$/.test(part)
        ? <Link key={i} to={`/leads?q=${part}`} onClick={onNavigate} className="font-medium text-brand-700 underline dark:text-brand-300">{part}</Link>
        : part);
  const inline = (line: string, key: number): ReactNode => (
    <Fragment key={key}>
      {line.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
        /^\*\*[^*]+\*\*$/.test(part) ? <strong key={i}>{links(part.slice(2, -2))}</strong> : <Fragment key={i}>{links(part)}</Fragment>)}
    </Fragment>
  );
  const blocks: ReactNode[] = [];
  let list: string[] = [];
  const flush = () => {
    if (list.length) blocks.push(<ul key={blocks.length} className="my-1 list-disc space-y-0.5 pl-5">{list.map((l, i) => <li key={i}>{inline(l, i)}</li>)}</ul>);
    list = [];
  };
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    const bullet = /^([-*•]|\d+\.)\s+(.*)$/.exec(line);
    if (bullet) { list.push(bullet[2]!); continue; }
    flush();
    if (line) blocks.push(<p key={blocks.length} className="my-1">{inline(line.replace(/^#+\s*/, ''), 0)}</p>);
  }
  flush();
  return <div className="leading-relaxed">{blocks}</div>;
}
